﻿import { extend } from './helpers.js';
import Map from '../../../vendor/ol/Map';
import View from '../../../vendor/ol/View';
import WMTSTileGrid from '../../../vendor/ol/tilegrid/WMTS';
import Overlay from '../../../vendor/ol/Overlay';
import Point from '../../../vendor/ol/geom/Point';
import { Tile as TileLayer, Vector as VectorLayer } from '../../../vendor/ol/layer';
import { OSM, WMTS, TileDebug, Vector as VectorSource } from '../../../vendor/ol/source';
import { defaults as InteractionDefaults, MouseWheelZoom } from '../../../vendor/ol/interaction';
import { Attribution, FullScreen, ScaleLine, Control, Zoom, defaults as ControlDefaults } from '../../../vendor/ol/control';
import Feature from '../../../vendor/ol/Feature';
import { circular } from '../../../vendor/ol/geom/Polygon';
import { getCenter } from '../../../vendor/ol/extent';

import { Projection, transform } from '../../../vendor/ol/proj';
import proj4 from '../../../vendor/proj4';
import { register } from '../../../vendor/ol/proj/proj4';

import GeoJSON from '../../../vendor/ol/format/GeoJSON';
import { Circle as CircleStyle, Fill, Stroke, Style, Text, Icon } from '../../../vendor/ol/style';

import Popover from '../../../vendor/bootstrap/js/dist/popover.js';
import { GeometryCollection } from '../../../vendor/ol/geom.js';

// https://devblogs.microsoft.com/typescript/typescript-and-babel-7/
// https://stackoverflow.com/questions/40029113/syntaxerror-import-and-export-may-appear-only-with-sourcetype-module-g
// https://opendata.lantmateriet.se/#mykeys
// https://www.skogsstyrelsen.se/sjalvservice/karttjanster/geodatatjanster/

// OpenLayers 7
export default class FurilloOpenLayersMap {

    constructor(element, options) {

        const defaults = {
            apiUrl: '',
            mapType: '',
            onMapLoaded: function(instance) {}
        };

        this.element = element;        
        this.map = null;
        this.options = extend(defaults, options);
        this.popover = undefined;
        this.gpslocation = null;

        if (this.options.apiUrl.length === 0) {
            this.options.apiUrl = this.element.getAttribute('data-url');
        }

        if (this.options.mapType.length === 0) {
            this.options.mapType = this.element.getAttribute('data-maptype');
        }

        this.mouseWheelOverlay = {
            element: element.querySelector('.map-ctrl-zoom-overlay'),
            eventCount: 0
        };

        this.init();
    }

    setupOverlay() {     
        const self = this;

        var element = document.createElement('div');
        element.id = 'popup';

        const overlay = new Overlay({
            id: 'popup',
            element: element,
            positioning: 'bottom-center',
            stopEvent: false
        });

        self.map.addOverlay(overlay);
    }

    disposePopover() {
        const self = this;

        if (self.popover) {
            self.popover.dispose();
            self.popover = undefined;            
        }
    }

    polygonStyle(feature, resolution) {                
        const visible = feature.get('visible');
        if (visible === false) {
            return new Style(null); // Hide the feature
        }

        // TODO: Resolution hide + string management?
        return new Style({
            stroke: new Stroke({
                color: feature.get('style').stroke.color,
                width: feature.get('style').stroke.width
            }),
            fill: new Fill({
                color: feature.get('style').fill.color
            }),
            text: new Text({
                textAlign: 'center',
                textBaseline: 'middle',
                font: 'normal 15px/1 Poppins',
                text: feature.get('name'),
                fill: new Fill({ color: feature.get('style').text.color }),
                stroke: new Stroke({ color: feature.get('style').text.stroke.color, width: feature.get('style').text.stroke.width }),
                offsetX: undefined,
                offsetY: undefined,
                placement: 'point',
                maxAngle: undefined,
                overflow: undefined,
                rotation: 0
            })            
        });
    }

    lineStyle(feature, resolution) {  
        const self = this;

        const visible = feature.get('visible');
        if (visible === false) {
            return new Style(null); // Hide the feature
        }

        // TODO: Resolution hide + string management?
        const lineStyle = new Style({
            stroke: new Stroke({
                color: feature.get('style').stroke.color,
                width: feature.get('style').stroke.width,
                lineCap: 'square',
                lineDash: [20, 10]
            }),
            text: new Text({
                textAlign: 'center',
                textBaseline: 'middle',
                font: 'bold 11px/1 Poppins',
                text: feature.get('label'),
                fill: new Fill({ color: feature.get('style').text.color }),
                stroke: new Stroke({ color: feature.get('style').text.stroke.color, width: feature.get('style').text.stroke.width }),
                offsetX: undefined,
                offsetY: 10,
                placement: 'line',
                maxAngle: undefined,
                overflow: undefined,
                rotation: 0
            })            
        });

        // Check if the line should have an icon
        const icon = feature.get('icon');
        if (!icon || icon.length === 0)
            return lineStyle;

        // Double check that a point was added when the feature was created
        const geometries = feature.getGeometry().getGeometries();
        if (geometries.length === 1)
            return lineStyle;

        // Define the line style only for the line
        lineStyle.setGeometry(geometries[0]);

        // Get scale
        // (1 / Math.pow(resolution, 1 / 3))
        var iconScale = 0.18;
        if (resolution > 11)
            iconScale = 0.12;

        if (resolution > 40)
            iconScale = 0.08;

        //console.log(resolution);
        
        const iconStyle = new Style({            
            image: new Icon({
                src: '/ui/default/img/map-icons/' + icon + '.png',
                offset: [0, 0],
                opacity: 1,
                scale: iconScale,
                anchor: [0.5, 0.5],
                anchorXUnits: 'fraction',
                anchorYUnits: 'fraction'
            })
        });

        iconStyle.setGeometry(geometries[1]);

        return [lineStyle, iconStyle];
    }
    
    pointStyle(feature, resolution) {
        const self = this;

        const visible = feature.get('visible');
        if (visible === false) {
            return new Style(null); // Hide the feature
        }

        const label = feature.get('label');
        if (!label || label.length === 0) {

            let icon = feature.get('icon');
            if (!icon || icon.length === 0)
                icon = 'poi-default';

            
            // Scale up based on resolution: (1 / Math.pow(resolution, 1 / 3))
            return new Style({
                image: new Icon({
                    src: '/ui/default/img/map-icons/' + icon + '.png',
                    offset: [0, 0],
                    opacity: 1,
                    scale: resolution > 20 ? 0.15 : 0.25,
                    anchor: [0.5, 0.95],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'fraction',
                })
            });
        }

        // cache https://stackoverflow.com/questions/57492200/scaling-the-icon-image-size-to-an-absolute-value

        const createTextStyle = function (feature, resolution) {
            return new Text({
                textAlign: 'center',
                textBaseline: 'middle',
                font: 'bold 11px/1 Poppins',
                text: feature.get('label'),
                fill: new Fill({ color: 'black' }),
                stroke: undefined,
                offsetX: 0,
                offsetY: 1,
                placement: 'point',
                maxAngle: undefined,
                overflow: undefined,
                rotation: 0                
            });
        };
                
        //https://openlayers.org/en/latest/examples/vector-labels.html
        return new Style({
            image: new CircleStyle({
                radius: resolution > 15 ? 6 : 12,
                fill: new Fill({ color: 'rgba(252, 125, 32, 1)' }),
                stroke: new Stroke({ color: 'rgba(252, 125, 32, 1)', width: 1 })
            }),
            text: resolution > 15 ? null : createTextStyle(feature, resolution),
        });
    }
    
    addPolygonFeatures() {
        const self = this;

        var vectorLayer = new VectorLayer({
            source: new VectorSource({
                url: '/api/map/geojson/polygons',
                format: new GeoJSON(),
                attributions: '©Frostvikens Skoterklubb'
            }),
            style: self.polygonStyle,
            useSpatialIndex: false            
        });

        self.map.addLayer(vectorLayer);
    }

    addLineFeatures() {
        const self = this;

        var vectorLayer = new VectorLayer({
            source: new VectorSource({
                url: '/api/map/geojson/lines',
                format: new GeoJSON(),
                useSpatialIndex: false
            }),
            style: self.lineStyle
        });

        vectorLayer.getSource().addEventListener('featuresloadend', function (event) {
            
            const features = event.features;
            if (!features)
                return;

            for (const feature of features) {
                
                let icon = feature.get('icon');
                if (!icon || icon.length === 0)
                    continue;

                // If there's an icon, expand the simple LineString geometry
                // to a collection that also contains a point for the icon placement
                let lineString = feature.getGeometry();
                
                let coordinates = lineString.flatCoordinates;
                let midIndex = (coordinates.length / 2);
                if (midIndex % 2 !== 0) 
                    midIndex++;               

                let center = [coordinates[midIndex], coordinates[midIndex + 1]];
                   
                let geometryCollection = new GeometryCollection([
                    lineString,
                    new Point(center)
                ]);
                
                feature.setGeometry(geometryCollection);                
            }
        });

        self.map.addLayer(vectorLayer);
    }

    addPointFeatures() {
        const self = this;

        var vectorLayer = new VectorLayer({
            source: new VectorSource({
                url: '/api/map/geojson/points',
                format: new GeoJSON(),
                useSpatialIndex: false
            }),
            style: self.pointStyle
        });

        vectorLayer.getSource().addEventListener('featuresloadend', function (event) {

            if (!window.location.search || window.location.search.length === 0)
                return;

            const params = new URLSearchParams(window.location.search);
            if (!params)
                return;

            const id = params.get('featureid');            
            if (!id || id.length === 0)
                return;
            
            const features = event.features;            
            if (!features)
                return;

            for (const feature of features) {
                
                if (feature.get('id') !== id)
                    continue;

                feature.set('visible', true);

                self.map.getView().fit(feature.getGeometry().getExtent(), self.map.getSize());
                break;
            }
        });

        self.map.addLayer(vectorLayer);        
    }

    setLayerVisibility(currentType, currentTag, visible) {
        const self = this;

        var isVisibleByOtherTag = function (type, tags) {
            const visibleTags = [];
            document.querySelectorAll('.layers-control input[type="checkbox"][name="' + type + '"]:checked').forEach(checkbox => {
                visibleTags.push(checkbox.value);
            });

            if (visibleTags.length === 0)
                return false;

            let visible = false;
            for (const tag of tags) {
                visible = visibleTags.includes(tag);

                if (visible === true)
                    break;
            }

            return visible;
        };

        self.map.getLayers().forEach(function (layer) {

            var source = layer.getSource();
            if (!source || typeof source.forEachFeature !== "function")
                return true;

            source.forEachFeature(function (feature) {

                const id = feature.get('id');
                if (!id || id.length === 0)
                    return false;

                const type = feature.get('type');
                if (!type || type.length === 0 || type !== currentType)
                    return false;

                // If has tags set, check that they include the current tag
                const tags = feature.get('tags');
                if (!tags || tags.length === 0 || !tags.includes(currentTag))
                    return false;

                if (feature.get('visible') === visible)
                    return false;

                if (!visible && isVisibleByOtherTag(currentType, tags)) {
                    // Double check that this feature isn't active from another tag
                    // then the current one being unchecked.
                    return false;
                }

                feature.set('visible', visible);
            });
        });
    };

    addLayersControl() {
        const self = this;

        var handleLayerFilterChange = function (e) {

            const currentType = e.target.name;
            const currentTag = e.target.value;
            const visible = e.target.checked;

            self.setLayerVisibility(currentType, currentTag, visible);            
        };        

        var element = document.createElement('div');
        element.className = 'layers-control dropdown ol-unselectable ol-control';
        
        fetch('/partials/map/layercontrol').then(function (response) {
            return response.text();
        }).then(function (html) {
            element.innerHTML = html;

            element.querySelectorAll('input[type="checkbox"]').forEach(checkbox => { checkbox.addEventListener('change', handleLayerFilterChange, false) });

            var LayersControl = new Control({
                element: element
            });

            self.map.addControl(LayersControl);

            document.querySelectorAll('.layers-control input[type="checkbox"]').forEach(checkbox => {
                let currentType = checkbox.name;
                let currentTag = checkbox.value;
                let visible = checkbox.checked;

                //if (currentType === 'line' || currentType === 'polygon') {
                //    self.setLayerVisibility(currentType, currentTag, visible);
                //}
            });
        }).catch(function (err) {
            console.warn('Something went wrong.', err);
        });        
    }

    addGpsPositioning() {
        const self = this;

        // Get position from browser and enable "center on"
        // Example: https://openlayers.org/workshop/en/mobile/geolocation.html
        // Rotation: https://openlayers.org/workshop/en/mobile/compass.html (not implemented but there's an icon)

        const ensureControl = function () {

            let hasControl = false;
            self.map.getControls().forEach(control => {
                if (control.element && control.element.classList.contains('locate-control')) {
                    hasControl = true;
                    return false;
                }
            });

            if (hasControl)
                return;            

            const locate = document.createElement('div');
            locate.className = 'ol-control ol-unselectable locate-control';
            locate.innerHTML = '<button title="Centrera kartan på min position.">◎</button>';

            locate.addEventListener('click', function () {
                if (!self.gpslocation) 
                    return;
                
                const source = self.gpslocation.getSource();
                if (!source || source.isEmpty())
                    return;                

                self.map.getView().fit(source.getExtent(), {                    
                    duration: 500,
                });
            });

            self.map.addControl(new Control({ element: locate, }));
        };

        self.gpslocation = new VectorLayer({
            source: new VectorSource(),
            style: new Style({
                fill: new Fill({
                    color: 'rgba(78, 120, 181, 0.2)',
                }),
                stroke: new Stroke({
                    color: 'rgba(193, 193, 193, 0.4)', width: 1
                }),
                image: new Icon({
                    src: '/ui/default/img/map-icons/location.png',
                    offset: [0, 0],
                    opacity: 1,
                    scale: 0.20,
                    anchor: [0.5, 0.5],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'fraction',
                }),
            }),            
        });
        self.map.addLayer(self.gpslocation);

        self.gpslocation.setZIndex(1000);
        
        navigator.geolocation.watchPosition(
            function (pos) {
                const coords = [pos.coords.longitude, pos.coords.latitude];
                const accuracy = circular(coords, pos.coords.accuracy);

                const source = self.gpslocation.getSource();
                source.clear(true);
                
                source.addFeatures([
                    new Feature(accuracy.transform('EPSG:4326', self.map.getView().getProjection())),
                    new Feature(new Point(transform(coords, 'EPSG:4326', self.map.getView().getProjection())))
                ]);

                ensureControl();
            },
            function (error) {                
                console.log('Failed to init geolocation', error.message); // Could be things like user denied, headers

                // Remove for other functionality to know we have no positioning
                self.gpslocation = null;
            },
            {
                enableHighAccuracy: true,
            }
        );



    }

    transformCoordinates(lat, lng, destination) {

        if (!destination) {
            destination = 'EPSG:3857';
        }

        // LatLng is reversed in OpenLayers
        return transform([lng, lat], 'EPSG:4326', destination);
        // EPSG:4326 = WGS84 decimal
        // EPSG:3857 = EPSG:900913 = WGS84 Web Mercator
        // EPSG:3006 = SWEREF99
    }

    init() {
        const self = this;

        proj4.defs("EPSG:3006", "+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs");
        register(proj4);

        var debugCoordinates = [];

        var loadMap = function () {

            // Setup our custom controls
            //const mouseWheelZoom = new MouseWheelZoom({
            //    onFocusOnly: true,
            //    handleEvent: (event) => {
            //        console.log(event);
            //    }
            //});

            //mouseWheelZoom.addEventListener('change', event => { console.log(event) });

            const controls = new ControlDefaults({ attribution: false, rotate: false, zoom: false }).extend([new Zoom({ zoomInTipLabel: 'Zooma in', zoomOutTipLabel: 'Zooma ut' }), new Attribution({ tipLabel: 'Tillskrivningar', collapsible: true }), new ScaleLine({ units: 'metric' }), new FullScreen({ tipLabel: 'Aktivera/inaktivera helskärmsläge' }), new MouseWheelZoom({ onFocusOnly: true })]);

            var loadLantmateriet = function() {

                const extent = [-1200000, 4700000, 2600000, 8500000];
                                
                const wmts = new TileLayer({
                    extent: extent,
                    source: new WMTS({
                        url: self.options.apiUrl,
                        layer: 'topowebb',
                        format: 'image/png',
                        matrixSet: '3006',
                        tileGrid: new WMTSTileGrid({
                            tileSize: 256,
                            extent: extent,
                            resolutions: [4096.0, 2048.0, 1024.0, 512.0, 256.0, 128.0, 64.0, 32.0, 16.0, 8.0],
                            matrixIds: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]
                        }),
                        version: '1.0.0',
                        style: 'default',
                        crossOrigin: 'anonymous',
                        attributions: '©<a href="https://www.lantmateriet.se" rel="noreferrer" target="_blank">Lantmäteriet</a> Topografisk webbkarta Visning, Översiktlig - v1',
                        minZoom: 0,
                        maxZoom: 9
                    })
                });

                const projection = new Projection({
                    code: 'EPSG:3006',
                    extent: extent,
                    units: 'metric'
                });

                // Do not restrict the view to the resolutions (10 steps) used for fetching the tiles, thus enabling "over zoom"
                // for the first and last resolution tiles (zoom without fetching any tiles from source).
                self.map = new Map({
                    target: self.element.id,
                    layers: [wmts],
                    logo: false,
                    interactions: InteractionDefaults({ altShiftDragRotate: false, pinchRotate: false }),
                    view: new View({
                        extent: extent,
                        center: self.transformCoordinates(64.706881, 14.017353, projection.getCode()),
                        zoom: 8,
                        minZoom: 0,
                        maxZoom: 13,                                             
                        projection: projection
                    }),
                    controls: controls
                });
            };

            var loadOpenStreetMap = function () {
                const osm = new TileLayer({
                    source: new OSM()
                });

                self.map = new Map({
                    target: self.element.id,
                    layers: [osm],
                    interactions: InteractionDefaults({ altShiftDragRotate: false, pinchRotate: false }),
                    view: new View({
                        center: self.transformCoordinates(64.706881, 14.017353),
                        zoom: 10,                        
                        minZoom: 4,
                        maxZoom: 16
                    }),
                    controls: controls                    
                });
            };


            if (self.options.mapType === 'OSM') {
                loadOpenStreetMap();            
            }
            else if (self.options.mapType === 'LM') {
                loadLantmateriet();
            }            

            self.map.addEventListener('click', event => {

                // Temp
                const coordinate = event.coordinate;
                //console.log(coordinate);
                //console.debug(transform(coordinate, event.map.getView().getProjection().getCode(), 'EPSG:4326'));
                debugCoordinates.push(transform(coordinate, event.map.getView().getProjection().getCode(), 'EPSG:4326'));
                console.debug(JSON.stringify(debugCoordinates));

                const transformed = transform(coordinate, event.map.getView().getProjection().getCode(), 'EPSG:4326');
                document.querySelector('#map .layers-control .latest-coordinate span').textContent = transformed[1] + ' ' + transformed[0]                               

                self.disposePopover();
                const feature = self.map.forEachFeatureAtPixel(event.pixel, function (feature) {
                    return feature;
                });
                
                if (!feature)
                    return;
                
                const popoverContent = feature.get('popoverContent');
                if (!popoverContent || popoverContent.length === 0)
                    return;

                const overlay = self.map.getOverlayById('popup');
                if (!overlay)
                    return;              

                overlay.setPosition(event.coordinate);

                self.popover = new Popover(overlay.getElement(), {
                    placement: 'top',
                    html: true,
                    content: popoverContent,
                    container: '#' + self.element.id
                });
                self.popover.show();
            });

            // TODO: Change pointer when clickable
            //self.map.addEventListener('pointermove', event => {

            //    const pixel = self.map.getEventPixel(event.originalEvent);
                
            //    const feature = self.map.forEachFeatureAtPixel(pixel, function (feature) {
            //        return feature;
            //    });

            //    if (!feature) {
            //        return;
            //    }
            //});

            self.map.addEventListener('movestart', event => { self.disposePopover(); });

            // Add custom overlay for mouse wheel zoom
            self.map.addEventListener('wheel', event => {

                if (document.fullscreenElement || !self.mouseWheelOverlay || !self.mouseWheelOverlay.element || !event.originalEvent)
                    return;

                self.mouseWheelOverlay.eventCount++;

                if (self.mouseWheelOverlay.eventCount < 3)
                    return;

                if (event.originalEvent.ctrlKey) {                    
                    self.mouseWheelOverlay.element.style.display = 'none';
                    self.mouseWheelOverlay.eventCount = 0;
                    return;
                }

                event.preventDefault();
                event.stopPropagation();

                if (self.mouseWheelOverlay.element.style.display === 'flex')
                    return;                

                self.mouseWheelOverlay.element.style.display = 'flex';                
                setTimeout(function () {                    
                    self.mouseWheelOverlay.element.style.display = 'none';
                    self.mouseWheelOverlay.eventCount = 0;
                }, 1000);


            });

            self.options.onMapLoaded(self);

            self.addGpsPositioning();
            self.setupOverlay();
            self.addPolygonFeatures();
            self.addLineFeatures();
            self.addPointFeatures();
            self.addLayersControl();            
        };

        // https://openstreetmap.be/en/projects/howto/openlayers.html

        //const sizeObserver = new ResizeObserver(() => {
        //    map.updateSize()
        //})
        //sizeObserver.observe(mapElement)

        // called when the map is destroyed
        // sizeObserver.disconnect()


        //var markers = new ol.layer.Vector({
        //    source: new ol.source.Vector(),
        //    style: new ol.style.Style({
        //        image: new ol.style.Icon({
        //            anchor: [0.5, 1],
        //            src: 'marker.png'
        //        })
        //    })
        //});
        //map.addLayer(markers);

        //var marker = new ol.Feature(new ol.geom.Point(ol.proj.fromLonLat([106.8478695, -6.1568562])));
        //markers.getSource().addFeature(marker);


        loadMap();        
    }
}
